import React from 'react';
import { X, ArrowUpRight, ArrowDownLeft, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Transaction } from '../App';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  handyPrice?: number | null;
}

export function TransactionDetailsModal({ isOpen, onClose, transaction, handyPrice }: TransactionDetailsModalProps) {
  if (!isOpen || !transaction) return null;

  const hash = transaction.hash;
  const amount = transaction.amount ?? 0;
  const usdValue = handyPrice ? Math.abs(amount * handyPrice) : null;

  const copyHash = async () => {
    if (!hash) return;
    try {
      await navigator.clipboard.writeText(hash);
      toast.success('트랜잭션 해시가 복사되었습니다.');
    } catch (error) {
      toast.error('복사에 실패했습니다.');
    }
  };

  const openExplorer = () => {
    window.open('https://handyscan.voltron.world/transactions', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-2xl max-w-sm w-full max-h-[90vh] overflow-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="font-bold text-lg text-foreground">트랜잭션 상세</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status */}
          <div className="text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              amount > 0
                ? 'bg-green-500/20 border-2 border-green-500/30'
                : 'bg-red-500/20 border-2 border-red-500/30'
            }`}>
              {amount > 0 ? (
                <ArrowDownLeft className="w-8 h-8 text-green-600" />
              ) : (
                <ArrowUpRight className="w-8 h-8 text-red-600" />
              )}
            </div>
            <h3 className="font-bold text-xl text-foreground mb-2">{transaction.type || '트랜잭션'}</h3>
            <div className={`text-3xl font-bold mb-1 ${
              amount > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {amount > 0 ? '+' : ''}{Math.abs(amount).toFixed(2)} wHANDY
            </div>
            {usdValue !== null && (
              <p className="text-muted-foreground text-sm">≈ ${usdValue.toFixed(2)} USD</p>
            )}
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div className="bg-muted rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-muted-foreground text-sm">트랜잭션 해시</span>
                {hash && (
                  <button
                    onClick={copyHash}
                    className="text-primary hover:text-primary/80 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="font-mono text-xs text-foreground break-all">
                {hash || '온체인 해시 없음(내부 처리)'}
              </p>
            </div>

            {transaction.createdAt && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted rounded-xl p-4">
                  <p className="text-muted-foreground text-sm mb-1">날짜</p>
                  <p className="font-medium text-foreground">
                    {new Date(transaction.createdAt).toLocaleDateString('ko-KR', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                <div className="bg-muted rounded-xl p-4">
                  <p className="text-muted-foreground text-sm mb-1">시간</p>
                  <p className="font-medium text-foreground">
                    {new Date(transaction.createdAt).toLocaleTimeString('ko-KR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            )}

            <div className="bg-muted rounded-xl p-4">
              <p className="text-muted-foreground text-sm mb-1">상태</p>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${transaction.status === 'failed' ? 'bg-red-500' : 'bg-green-500'}`}></div>
                <span className={`font-medium ${transaction.status === 'failed' ? 'text-red-600' : 'text-green-600'}`}>
                  {transaction.status === 'failed' ? '실패' : '완료'}
                </span>
              </div>
            </div>

            <div className="bg-muted rounded-xl p-4">
              <p className="text-muted-foreground text-sm mb-1">네트워크</p>
              <p className="font-medium text-foreground">Handy Mainnet</p>
            </div>

            {transaction.blockNumber !== undefined && (
              <div className="bg-muted rounded-xl p-4">
                <p className="text-muted-foreground text-sm mb-1">블록 번호</p>
                <p className="font-medium text-foreground">#{transaction.blockNumber}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={openExplorer}
              className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>탐색기에서 보기</span>
            </button>

            <button
              onClick={onClose}
              className="w-full bg-muted text-foreground py-3 rounded-xl font-medium hover:bg-accent transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
